import axios, { AxiosStatic } from 'axios';
import _Vue from 'vue';
import { addUserIdToData } from './utils/addUserIdToData';
import { isLoginPage } from './utils/isLoginPage';
import { keycloakAuthService } from '@/services/keycloakAuth';
import {isAuthCallbackPage} from "@/utils/isAuthCallbackPage";
import {notification} from "ant-design-vue";
import { i18n } from '@/services/i18n';

declare module 'vue/types/vue' {
  // tslint:disable-next-line: interface-name
  interface Vue {
    $http: AxiosStatic;
  }
}

const createAxios = () => {
  const client = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_URL,
  })

  client.interceptors.request.use(
      async (config) => {
          const auth  = localStorage.getItem('AUTH')
          if (!auth) {
              delete config.headers.Authorization
              return config;
          }

          const { access_token, user_id: userId } = JSON.parse(auth);

          if (process.env.VUE_APP_AUTH_MODE === 'keycloak') {
              const token = await keycloakAuthService.loadAccessToken();

              config.headers.Authorization = `Bearer ${token}`;
          } else {
              config.headers['Authorization'] = `Bearer ${access_token}`
          }

          const params = addUserIdToData(config, userId);

          if (params) {
              return {
                  ...config,
                  params,
              }
          }

          return config;
      },
      (error) => Promise.reject(error),
  )

    let refreshTokenPromise: Promise<void> | null = null;

    client.interceptors.response.use(
        (res) => res,
        (error) => {
            if (error.response && error.response.status === 401 && !isLoginPage() && !isAuthCallbackPage()) {
                if (process.env.VUE_APP_AUTH_MODE === 'keycloak') {
                    if (!refreshTokenPromise) {
                        refreshTokenPromise = keycloakAuthService.refreshToken()
                            .catch(() => {
                                keycloakAuthService.logout().finally(() => {
                                    notification.error({
                                        message: i18n.t('auth.errors.errorCheckAccess') as string,
                                        description: '',
                                        duration: 7,
                                    });

                                    setTimeout(() => {
                                        window.location.replace('/login');
                                    }, 5000);
                                });
                            })
                            .finally(() => {
                                refreshTokenPromise = null;
                            });
                    }
                } else {
                    localStorage.removeItem('AUTH');
                    localStorage.removeItem('userData');

                    window.history.replaceState(null, '', '/login');
                }
            }

            return Promise.reject(error);
        }
    );

    return client;
};

const AXIOS = createAxios();

export function AxiosPlugin<AxiosPlugOptions>(Vue: typeof _Vue, options?: AxiosPlugOptions): void {
  Vue.prototype.$http = AXIOS;
}
